import { useAxios, useSubjectMember } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { useOutletContext } from 'react-router-dom-original'
import styled, { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout/flex/Flex'
import { Markdown } from '@/@bothrs-ui/web/components/markdown/Markdown'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { mixpanel } from '@/mixpanel'
import { QuestionnaireAnswers } from '@/modules/questionnaire/components/questionnaire-answers/QuestionnaireAnswers'
import { useQuestionnaireCareplanActivity } from '@/modules/questionnaire/hooks/useQuestionnaireCareplanActivity'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { useToast } from '@/modules/shared/hooks/useToast'
import { TaskDetailLayout } from '@/modules/task/components/detail-layout/TaskDetailLayout'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { useNavigate } from '@/router-wrapper'
import { isIntramuros } from '@/utils/intramuros'
import { buildQuestionnaireTaskEditUrl } from '../../utils/task.utils'

import type { TaskOutletContext } from '../../pages/TaskRoot.page'
import type { QuestionnaireTask } from '../../types/task.types'

type Props = {
  task: QuestionnaireTask
  statusDisplay: string
  refetch?: () => void
  variant?: 'reconfirmation' | 'prefill'
}

export const QuestionnaireReconfirmDetail = ({ task, statusDisplay, refetch, variant = 'reconfirmation' }: Props) => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()
  const navigate = useNavigate()
  const subject = useSubjectMember()

  const axios = useAxios()
  const isLoggedInForMandatee = useLoggedInForMandatee()
  const { showSuccessToast, showErrorToast } = useToast()
  const { reloadAndSelectTask } = useOutletContext<TaskOutletContext>()

  const { data: questionnaireActivity, isLoading } = useQuestionnaireCareplanActivity('' + task.careplanActivityId)

  // Template variables
  // ------------------------------------------------------------------------- /

  // Use the enriched response from the careplan-activity hook (includes latest
  // health parameter values from Synops).
  const questionnaireResponse = questionnaireActivity?.questionnaireResponse
  const questionnaire = questionnaireActivity?.questionnaire

  // Distinct answers the patient opened for editing, kept in sessionStorage so
  // the set survives the navigation to the edit flow and back. Powers the
  // edited/total ratio on the `${variant}_submit` event.
  // The key is only cleared on submit, so abandoning the flow without
  // submitting leaves a stale entry. It is scoped per careplanActivityId and
  // bounded by sessionStorage's tab lifetime, so the leak is harmless.
  const editedLinkIdsKey = `${variant}-edited-link-ids-${task.careplanActivityId}`

  const readEditedLinkIds = (): string[] => {
    try {
      const stored = JSON.parse(sessionStorage.getItem(editedLinkIdsKey) ?? '[]')
      return Array.isArray(stored) ? stored : []
    } catch {
      return []
    }
  }

  // Interaction methods
  // ------------------------------------------------------------------------- /
  const handleEditAnswer = (linkId: string) => {
    mixpanel.track(`${variant}_answer_edit`, {
      questionnaire_slug: questionnaire?.name,
      is_mandate: isLoggedInForMandatee,
      is_intramuros: isIntramuros,
    })

    const editedLinkIds = readEditedLinkIds()
    if (!editedLinkIds.includes(linkId)) {
      sessionStorage.setItem(editedLinkIdsKey, JSON.stringify([...editedLinkIds, linkId]))
    }

    const url = buildQuestionnaireTaskEditUrl(task, linkId)
    if (url) {
      navigate(url)
    }
  }

  const handleConfirm = async () => {
    mixpanel.track(`${variant}_submit`, {
      questionnaire_slug: questionnaire?.name,
      is_mandate: isLoggedInForMandatee,
      is_intramuros: isIntramuros,
      edited_answer_count: readEditedLinkIds().length,
      total_answer_count: questionnaireResponse?.answers?.length ?? 0,
    })
    sessionStorage.removeItem(editedLinkIdsKey)

    try {
      await axios.post(`/careplan-activity/${task.careplanActivityId}/questionnaire-response`, {
        questionnaireId: task.questionnaireId,
        answers: questionnaireResponse?.answers,
        status: 'completed',
      })

      refetch?.()

      showSuccessToast({
        message: t({
          id:
            variant === 'prefill' ? 'questionnaire-detail.toast.prefill' : 'questionnaire-detail.toast.reconfirmation',
          message: 'Vragenlijst bevestigd',
        }),
      })
      reloadAndSelectTask(task.id)
    } catch {
      showErrorToast({
        message: t({
          id:
            variant === 'prefill'
              ? 'questionnaire-detail.toast.prefill-error'
              : 'questionnaire-detail.toast.reconfirmation-error',
          message: 'Er ging iets mis bij het bevestigen van de vragenlijst',
        }),
      })
    }
  }

  // Render
  // ------------------------------------------------------------------------- /
  if (isLoading) {
    return <AnimatedLoader />
  }

  if (!questionnaireResponse || !questionnaire) {
    return null
  }

  return (
    <TaskDetailLayout>
      <TaskDetailLayout.Header task={task} statusLabel={statusDisplay} />

      <TaskDetailLayout.Content>
        {
          <FlexColumn gap={theme.UI.Spacing.Space4}>
            <StMessage>
              <Markdown
                content={
                  task.message?.replace(/\\n/g, '\n') ??
                  (variant === 'prefill'
                    ? t({
                        id: 'questionnaire-prefill-flow.overview.intro',
                        message:
                          'Beste {name},\n\nJe bent onlangs opgenomen geweest in ons ziekenhuis. Omdat je dezelfde vooropname vragenlijst opnieuw moet invullen, hebben we je eerdere antwoorden al ingevuld.\n\nKijk even na of alles nog klopt. Denk aan veranderingen in je medicatie of gezondheid. Is er niets veranderd? Dan kan je meteen bevestigen.',
                        values: {
                          name: subject?.givenName,
                        },
                      })
                    : t({
                        id: 'questionnaire-reconfirm-flow.overview.intro',
                        message:
                          'Beste {name},\n\nJe antwoorden in je vooropnamevragenlijst dateren van {date}. Ter voorbereiding van je opname is het belangrijk dat we weten of er recente wijzigingen zijn in je medisch dossier, zoals veranderingen in medicatie of nieuwe aandoeningen.\n\nBekijk je antwoorden en pas ze aan waar nodig. Is er niets veranderd? Dan kan je je antwoorden gewoon bevestigen.',
                        values: {
                          name: subject?.givenName,
                          date: formatLocale(questionnaireActivity?.createdAt, 'EEEE dd/MM/yyyy'),
                        },
                      }))
                }
                typography='BodyMediumRegular'
                color={theme.Colors.Neutrals.Darkest}
              />
            </StMessage>
            <Line />

            <QuestionnaireAnswers
              questionnaire={questionnaire}
              questionnaireResponse={questionnaireResponse}
              onEditAnswer={handleEditAnswer}
              editable={true}
            />
          </FlexColumn>
        }
      </TaskDetailLayout.Content>

      <TaskDetailLayout.Footer>
        <Button variant='Primary' onClick={handleConfirm}>
          {t({
            id: 'questionnaire-reconfirm-flow.overview.confirm',
            message: 'Antwoorden bevestigen',
          })}
        </Button>
      </TaskDetailLayout.Footer>
    </TaskDetailLayout>
  )
}

const StMessage = styled.div`
  padding: ${({ theme }) => theme.UI.SpacingPx.Space2} 0;
`
